/**
 * ProBadge Component - Displays gold Pro status badge with Western styling.
 * Maintains exact design consistency with existing wanted-poster theme.
 */

import { Star, Shield, Award, Swords } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ProBadgeProps } from "@/types";

/**
 * SheriffBadge - A standalone sheriff star badge visible to Pro users.
 * Use this when you want just the badge icon without the "Pro" label.
 */
export const SheriffBadge = ({ size = "sm" }: { size?: "sm" | "md" | "lg" }) => {
  const iconSizes = {
    sm: "h-5 w-5",
    md: "h-7 w-7",
    lg: "h-9 w-9",
  };
  const containerSizes = {
    sm: "w-7 h-7",
    md: "w-10 h-10",
    lg: "w-12 h-12",
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`relative flex items-center justify-center ${containerSizes[size]} rounded-full bg-gradient-to-br from-[hsl(45_85%_55%)] to-[hsl(35_75%_42%)] shadow-lg shadow-[hsl(45_85%_55%)/30] ring-2 ring-[hsl(45_85%_55%)/50]`}
          >
            <Shield className={`${iconSizes[size]} text-[hsl(25_50%_12%)] absolute`} />
            <Star className={`${iconSizes[size]} text-[hsl(25_50%_12%)] fill-current scale-75`} />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Sheriff's Badge — Pro Member</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const ProBadge = ({ size = "sm", showTooltip = true }: ProBadgeProps) => {
  const sizeClasses = {
    sm: "text-xs px-2 py-0.5 gap-1",
    md: "text-sm px-3 py-1 gap-1.5",
    lg: "text-base px-4 py-1.5 gap-2",
  };

  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  const badge = (
    <div
      className={`
        inline-flex items-center font-bold uppercase tracking-wider
        bg-gradient-to-r from-[hsl(45_85%_55%)] to-[hsl(35_75%_42%)]
        text-[hsl(25_50%_12%)] border border-[hsl(var(--paper-edge))]
        ${sizeClasses[size]}
      `}
      style={{
        boxShadow: "0 2px 4px hsl(25 40% 15% / 0.25)",
      }}
    >
      <Star className={`${iconSizes[size]} fill-current`} />
      <span>Pro</span>
    </div>
  );

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Pro Member - Sheriff's Badge</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * VerificationBadge - Shows hunter verification status
 */
interface VerificationBadgeProps {
  level: number;
  size?: "sm" | "md" | "lg";
}

export const VerificationBadge = ({ level, size = "sm" }: VerificationBadgeProps) => {
  if (level === 0) return null;

  const sizeClasses = {
    sm: "text-xs px-2 py-0.5 gap-1",
    md: "text-sm px-3 py-1 gap-1.5",
    lg: "text-base px-4 py-1.5 gap-2",
  };

  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  const labels = {
    1: "Verified",
    2: "Trusted",
    3: "Expert",
  };

  const colors = {
    1: "from-[hsl(130_50%_32%)] to-[hsl(130_40%_28%)]",
    2: "from-[hsl(200_70%_45%)] to-[hsl(200_60%_38%)]",
    3: "from-[hsl(45_85%_55%)] to-[hsl(35_75%_42%)]",
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`
              inline-flex items-center font-bold uppercase tracking-wider
              bg-gradient-to-r ${colors[level]}
              text-white border border-[hsl(var(--paper-edge))]
              ${sizeClasses[size]}
            `}
            style={{
              boxShadow: "0 2px 4px hsl(25 40% 15% / 0.25)",
            }}
          >
            <Shield className={`${iconSizes[size]} fill-current`} />
            <span>{labels[level]}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Verified Hunter - Level {level}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * FeaturedBadge - Shows on featured/pro priority bounties
 */
interface FeaturedBadgeProps {
  size?: "sm" | "md";
}

export const FeaturedBadge = ({ size = "sm" }: FeaturedBadgeProps) => {
  const sizeClasses = {
    sm: "text-[10px] px-2 py-0.5",
    md: "text-xs px-3 py-1",
  };

  return (
    <div
      className={`
        inline-flex items-center gap-1 font-bold uppercase tracking-widest
        bg-gradient-to-r from-[hsl(45_85%_55%)] to-[hsl(35_75%_42%)]
        text-[hsl(25_50%_12%)] border border-[hsl(var(--paper-edge))]
        ${sizeClasses[size]}
      `}
      style={{
        boxShadow: "0 2px 4px hsl(25 40% 15% / 0.25)",
      }}
    >
      <Award className={`${size === "sm" ? "h-3 w-3" : "h-4 w-4"} fill-current`} />
      Featured
    </div>
  );
};
